import {AppInitializeService} from '../app-initialize-service';
import {defaultTemplates} from './default-templates';
import {inject, Injectable} from '../di';
import {Logger} from '../logger';
import * as jsrender from 'jsrender';
import {LegacyLogService} from '../logger/legacy-log-service';
import {getText} from '../utils/get-text';

@Injectable()
export class TemplatesService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);

  /**
   * Cache of HTML templates
   */
  private readonly templates = new Map<string, string>();

  /**
   * Cache of HTML templates compiled by jsrender
   */
  private readonly templatesCompiled = new Map<string, JsViews.Template>();

  constructor() {
    window.templates = {};
    window.templates_cache = {};

    const templatesService = this;
    window.LoadTemplates = function (urls: string[]): JQuery.Promise<void> {
      return $.when(templatesService.loadTemplates(urls));
    };
    window.loadTemplate = function (url: string): JQuery.Promise<void> {
      return $.when(templatesService.loadTemplate(url));
    };
    window.loadTemplatesWrap = function (fn: JQuery.Deferred<any>): JQuery.Promise<void> {
      return $.when(fn);
    };
  }

  public init(): boolean {
    try {
      return this.addTemplates(defaultTemplates);
    } catch (err) {
      this.logger.critical('[TemplatesService] Unable to initialize templates', err);
      return false;
    }
  }

  /**
   * Get compiled template
   * @param templateName
   */
  public getCompiledTemplate(templateName: string) {
    return this.templatesCompiled.get(templateName);
  }

  /**
   * Get template source
   * @param templateName
   */
  public getTemplate(templateName: string) {
    return this.templates.get(templateName);
  }

  /**
   * Load several template by URLs. Template names will be extracted from file names.
   * @param templateUrls
   */
  public async loadTemplates(templateUrls: string[]) {
    try {
      const start = Date.now();
      const results = await Promise.allSettled(templateUrls.map((url) => this.loadTemplate(url)));
      const loaded = results.filter((r) => r.status === 'fulfilled').length;
      const duration = Date.now() - start;

      this.legacyLogService.add(`TPL: loaded ${loaded} templates in ${duration}ms`);

      this.logger.debug(`[TemplatesService] ${loaded} templates loaded in ${duration} ms`);
    } catch (err) {
      this.logger.error(`[TemplatesService] Unable to load templates. Unexpected error.`, templateUrls, err);
      return Promise.reject();
    }
  }

  /**
   * Load template by URL. Template name will be extracted from file name.
   * @param templateUrl
   */
  public async loadTemplate(templateUrl: string) {
    try {
      const templateName = templateUrl.split('/').pop()?.split('.').shift();
      if (!templateName) {
        this.logger.error(`[TemplatesService] Unable to extract template name from template URL: ${templateUrl}`);
        return Promise.reject();
      }

      if (this.templates.has(templateName)) {
        return Promise.resolve();
      }

      this.logger.debug(`[TemplatesService] Template loading started: ${templateUrl}`);

      const template = await getText(templateUrl);

      const addTemplateSuccess = this.addTemplate(templateName, template);
      if (!addTemplateSuccess) {
        // The main error already logged in `addTemplate`, so just add information about URL
        this.logger.warn(`[TemplatesService] Template url: ${templateUrl}`);
        return Promise.reject();
      }

      this.logger.debug(`[TemplatesService] Template loading finished: ${templateUrl}`);
    } catch (err) {
      this.logger.error(`[TemplatesService] Unable to load template ${templateUrl}. Unexpected error.`, err);
      return Promise.reject();
    }

    return Promise.resolve();
  }

  /**
   * Load a list of templates
   * @param templates hash object (map) where the key is a template name and value is template source code.
   * @returns false if one of the templates was failed to load. Error/exception will be logged by this function.
   * @private
   */
  public addTemplates(templates: {[templateName: string]: string}) {
    for (const [templateName, template] of Object.entries(templates)) {
      const addSuccess = this.addTemplate(templateName, template);
      if (!addSuccess) {
        return false;
      }
    }
    return true;
  }

  /**
   * Add template by its name and source code.
   * @param templateName
   * @param template template source code (HTML)
   * @return false if template was failed to load. Error/exception will be logged by this function.
   */
  public addTemplate(templateName: string, template: string): boolean {
    try {
      if (this.templatesCompiled.has(templateName)) {
        this.logger.warn(
          `[TemplatesService] Template duplicate. The "${templateName}" template has already been added and compiled.`,
        );
      }

      const compiledTemplate = jsrender.templates(template);

      this.templates.set(templateName, template);
      this.templatesCompiled.set(templateName, compiledTemplate);

      window.templates[templateName] = template;
      window.templates_cache[templateName] = compiledTemplate;
    } catch (err) {
      this.logger.error(`[TemplatesService] Unable to compile template: ${templateName}`, err);
      return false;
    }

    return true;
  }
}

declare global {
  interface Window {
    /**
     * Map of templates' source code
     */
    templates: Record<string, string>;

    /**
     * Map of compiled templates
     */
    templates_cache: Record<string, JsViews.Template>;
    /**
     * Load multiple templates by URLs
     * @param urls
     * @constructor
     * @deprecated use `TemplatesService.loadTemplates(urls)` instead
     */
    LoadTemplates: (urls: string[]) => JQuery.Promise<void>;
    /**
     * Load template by URLs
     * @param url
     * @deprecated use `TemplatesService.loadTemplate(url)` instead
     */
    loadTemplate: (url: string) => JQuery.Promise<void>;
    /**
     * Wrap into Jquery Promise object
     * @param fn
     * @deprecated
     */
    loadTemplatesWrap: (fn: JQuery.Deferred<any>) => JQuery.Promise<void>;
  }
  const templates: Record<string, string>;
  const templates_cache: Record<string, JsViews.Template>;
  const LoadTemplates: (urls: string[]) => JQuery.Promise<void>;
  const loadTemplate: (url: string) => JQuery.Promise<void>;
  const loadTemplatesWrap: (fn: JQuery.Deferred<any>) => JQuery.Promise<void>;
}
